import React, { Fragment, useState, useEffect } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import AppPagination from "../Common/AppPagination";
import { Row, Col, Card, Table, Badge, Dropdown, Modal, Button, Alert, Form } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import SignatureModal from "../Common/SignatureModal";
import EmailModal from "../Common/EmailModal";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";
import VerifiedCopyActions from "../Common/VerifiedCopyActions";
import usePermission from "../../../hooks/usePermission";

const BillingList = () => {
	const user = useSelector((state) => state.auth?.userDetails || {});
	const { hasPermission, isAdmin } = usePermission();
	const [billings, setBillings] = useState([]);
	const [workOrders, setWorkOrders] = useState([]);
	const [projects, setProjects] = useState([]);
	const [selectedBilling, setSelectedBilling] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const [previewingPdf, setPreviewingPdf] = useState(false);
	const [paymentSubmitting, setPaymentSubmitting] = useState(false);
	const [paymentBilling, setPaymentBilling] = useState(null);
	const [paymentAmount, setPaymentAmount] = useState("");
	const [deductionTypes, setDeductionTypes] = useState([]);
	const [advisedInvoiceFile, setAdvisedInvoiceFile] = useState(null);
	const [deductions, setDeductions] = useState([]);
	const [advanceSummary, setAdvanceSummary] = useState({
		totalAdvance: 0,
		advanceDescription: "",
		alreadyDeducted: 0,
		availableAdvance: 0,
	});
	const [showSignModal, setShowSignModal] = useState(false);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [emailInitialData, setEmailInitialData] = useState({});
	const [approveId, setApproveId] = useState(null);
	const [pagination, setPagination] = useState({
		currentPage: 1,
		totalPages: 1,
		totalCount: 0,
		limit: 10,
	});
	const [searchParams] = useSearchParams();

	const workOrderFromUrl = searchParams.get("workOrder") || "";
	const projectFromUrl = searchParams.get("project") || "";
	const paymentStatusFromUrl = searchParams.get("paymentStatus") || "";
	const [filters, setFilters] = usePersistentFilters("billings_filters", {
		search: "",
		project: projectFromUrl || "",
		workOrder: workOrderFromUrl || "",
		status: paymentStatusFromUrl || "",
		fromDate: "",
		toDate: "",
		page: 1,
		limit: 10,
	});
	const currentRole = String(user?.role?.name || user?.roleName || user?.role || "").toLowerCase();
	const canManageBillings =
		isAdmin ||
		hasPermission([
			"create:billings",
			"edit:billings",
			"approve:billings",
			"reject:billings",
			"delete:billings",
		]);
	const canAddBilling = isAdmin || hasPermission("create:billings");

	const [error, setError] = useState("");

	useEffect(() => {
		fetchWorkOrders();
		fetchProjects();
		fetchDeductionTypes();
		fetchBillings();
	}, [
		filters.search,
		filters.project,
		filters.workOrder,
		filters.status,
		filters.fromDate,
		filters.toDate,
		filters.page,
		filters.limit,
		workOrderFromUrl,
		projectFromUrl,
		paymentStatusFromUrl
	]);

	useEffect(() => {
		if (paymentStatusFromUrl) {
			setFilters((p) => ({ ...p, status: paymentStatusFromUrl, page: 1 }));
		}
	}, [paymentStatusFromUrl]);

	// ---------------------------
	// Fetch Deduction Types
	// ---------------------------
	const fetchDeductionTypes = async () => {
		try {
			const response = await api.get("/deduction-types");
			if (response.data?.success) {
				const active = (response.data.data || []).filter(
					(t) => t.status !== "Inactive"
				);
				setDeductionTypes(active);
			}
		} catch (error) {
			console.error("Error fetching deduction types:", error);
		}
	};

	// ---------------------------
	// Fetch Work Orders (Fixed)
	// ---------------------------
	const fetchWorkOrders = async () => {
		try {
			const response = await api.get("/work-orders?limit=1000");
			if (response.data.success) setWorkOrders(response.data.data);
		} catch (error) {
			console.error("Error fetching work orders:", error);
		}
	};

	const fetchProjects = async () => {
		try {
			const response = await api.get("/projects?limit=1000");
			if (response.data.success) setProjects(response.data.data || []);
		} catch (error) {
			console.error("Error fetching projects:", error);
		}
	};

	const availableWorkOrders = React.useMemo(() => {
		if (!filters.project) return workOrders;
		return workOrders.filter((wo) => {
			const pId = wo.project?._id || (typeof wo.project === "string" ? wo.project : null);
			return String(pId) === String(filters.project);
		});
	}, [workOrders, filters.project]);
	const workOrderMap = React.useMemo(() => {
		const map = {};
		workOrders.forEach((wo) => {
			map[wo._id] = wo;
		});
		return map;
	}, [workOrders]);

	// ---------------------------
	// Fetch All Billings
	// ---------------------------
	const fetchBillings = async () => {
		try {
			setLoading(true);
			setError("");

			const params = new URLSearchParams();
			if (filters.search) params.append("search", filters.search);
			if (filters.project) params.append("project", filters.project);
			if (filters.workOrder) params.append("workOrder", filters.workOrder);
			const effectiveStatus = filters.status || paymentStatusFromUrl;
			if (effectiveStatus) params.append("status", effectiveStatus);
			if (filters.fromDate) params.append("fromDate", filters.fromDate);
			if (filters.toDate) params.append("toDate", filters.toDate);

			params.append("page", filters.page);
			params.append("limit", filters.limit);

			const response = await api.get(`/billing?${params.toString()}`);

			if (response.data.success) {
				const validBillings = (response.data.data || []).filter(
					(b) =>
						(b.project && (b.project._id || b.project.projectName)) ||
						(b.workOrder?.project &&
							(b.workOrder.project._id || b.workOrder.project.projectName))
				);
				setBillings(validBillings);
				if (response.data.pagination) {
					setPagination(prev => ({
						...prev,
						...response.data.pagination
					}));
				}
			} else {
				setBillings([]);
			}
		} catch (error) {
			console.error("Error fetching billings:", error);
			setError(error.response?.data?.message || "Failed to fetch billings");
		} finally {
			setLoading(false);
		}
	};

	const handlePageChange = (page) => {
		setFilters(prev => ({ ...prev, page: page }));
	};

	const readBlobText = async (blob) => {
		if (!(blob instanceof Blob)) return "";
		if (typeof blob.text === "function") return blob.text();

		return new Promise((resolve) => {
			const reader = new FileReader();
			reader.onload = () => resolve(String(reader.result || ""));
			reader.onerror = () => resolve("");
			reader.readAsText(blob);
		});
	};

	const handlePreviewPDF = async (id) => {
		const previewWindow = window.open("", "_blank");
		try {
			setPreviewingPdf(true);
			if (previewWindow) {
				previewWindow.document.write("<p>Loading PDF preview...</p>");
			}

			const signedRes = await api.get(`/billing/${id}/pdf`, {
				params: { mode: "preview", signed: "1" },
				timeout: 30000,
				suppressErrorToast: true,
			});

			if (signedRes.data?.cached && signedRes.data?.url) {
				console.info("[billing-pdf] Preview served from S3 signed URL", {
					billingId: id,
					mode: signedRes.data.mode,
					fileName: signedRes.data.fileName,
				});

				if (previewWindow) {
					previewWindow.location.href = signedRes.data.url;
				} else {
					window.open(signedRes.data.url, "_blank");
				}
				return;
			}

			console.info("[billing-pdf] Preview S3 cache miss, falling back to backend PDF generation", {
				billingId: id,
				mode: signedRes.data?.mode,
			});

			const res = await api.get(`/billing/${id}/pdf`, {
				params: { mode: "preview" },
				responseType: "blob",
				timeout: 900000,
				suppressErrorToast: true,
			});

			// Validate: check if backend actually returned a PDF
			const contentType = res.headers["content-type"] || "";
			if (!contentType.includes("application/pdf")) {
				if (previewWindow) previewWindow.close();
				const errorText = await readBlobText(res.data);
				Swal.fire({
					icon: "error",
					title: "PDF Generation Failed",
					text: errorText
						? `Server error: ${errorText.slice(0, 200)}`
						: "The server could not generate the PDF. Please contact support.",
				});
				return;
			}

			const fileURL = window.URL.createObjectURL(
				new Blob([res.data], { type: "application/pdf" }),
			);

			if (previewWindow) {
				previewWindow.location.href = fileURL;
			} else {
				const link = document.createElement("a");
				link.href = fileURL;
				link.download = `INVOICE-${id}.pdf`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
			window.setTimeout(() => window.URL.revokeObjectURL(fileURL), 60000);
		} catch (err) {
			if (previewWindow) previewWindow.close();
			console.error("Invoice PDF preview error:", err);

			// Try to read error blob if present
			let errMsg = err.message || "Failed to preview Invoice PDF.";
			if (err.response?.data instanceof Blob) {
				try {
					const text = await readBlobText(err.response.data);
					if (text) errMsg = text;
				} catch (_) { }
			}

			Swal.fire({
				icon: "error",
				title: "Preview Failed",
				text: errMsg.slice(0, 300),
			});
		} finally {
			setPreviewingPdf(false);
		}
	};

	const handleDownloadExcel = async (id) => {
		try {
			const res = await api.get(`/billing/${id}/excel`, {
				responseType: "blob",
			});

			const url = window.URL.createObjectURL(
				new Blob([res.data], {
					type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				}),
			);

			const link = document.createElement("a");
			link.href = url;
			link.download = `INVOICE-${id}.xlsx`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			Swal.fire({
				icon: "success",
				title: "Downloaded!",
				text: "Invoice Excel downloaded successfully.",
				timer: 1500,
				showConfirmButton: false,
			});
		} catch (err) {
			console.error("Excel download error:", err);

			let errMsg = "Failed to download Proforma Invoice Excel.";
			if (err.response?.data instanceof Blob) {
				try {
					const text = await err.response.data.text();
					if (text) errMsg = text;
				} catch (_) { }
			}

			Swal.fire({
				icon: "error",
				title: "Download Failed",
				text: errMsg,
			});
		}
	};

	const downloadFullDataExcel = async (id) => {
		const res = await api.get(`/billing/${id}/excel`, {
			responseType: "blob",
			timeout: 300000,
		});

		const url = window.URL.createObjectURL(
			new Blob([res.data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			}),
		);

		const link = document.createElement("a");
		link.href = url;
		link.download = `INVOICE-${id}-FULL-DATA.xlsx`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.setTimeout(() => window.URL.revokeObjectURL(url), 3000);
	};

	const handleDownloadPIExcel = async (id) => {
		try {
			const res = await api.get(`/billing/${id}/piexcel`, {
				responseType: "blob",
			});

			const url = window.URL.createObjectURL(
				new Blob([res.data], {
					type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				}),
			);

			const link = document.createElement("a");
			link.href = url;
			link.download = `INVOICE-${id}.xlsx`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			Swal.fire({
				icon: "success",
				title: "Downloaded!",
				text: "Invoice Excel downloaded successfully.",
				timer: 1500,
				showConfirmButton: false,
			});
		} catch (err) {
			console.error("Excel download error:", err);

			Swal.fire({
				icon: "error",
				title: "Download Failed",
				text: "Failed to download Invoice Excel.",
			});
		}
	};
	const [downloadingPdf, setDownloadingPdf] = useState(false);

	const handleDownloadPDF = async (id) => {
		try {
			setDownloadingPdf(true);

			const signedRes = await api.get(`/billing/${id}/pdf`, {
				params: { signed: "1" },
				timeout: 30000,
			});

			if (signedRes.data?.cached && signedRes.data?.url) {
				console.info("[billing-pdf] Served from S3 signed URL", {
					billingId: id,
					mode: signedRes.data.mode,
					fileName: signedRes.data.fileName,
				});

				const link = document.createElement("a");
				link.href = signedRes.data.url;
				link.download = signedRes.data.fileName || `INVOICE-${id}.pdf`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				if (signedRes.data.mode === "download-lite") {
					try {
						await downloadFullDataExcel(id);
						Swal.fire({
							icon: "info",
							title: "Large Invoice Exported",
							text: "A lighter PDF was downloaded because the document is very large. The full billing data has also been downloaded in Excel format.",
						});
					} catch (excelError) {
						console.error("Full data Excel download error:", excelError);
						Swal.fire({
							icon: "warning",
							title: "PDF Downloaded",
							text: "The invoice PDF was very large, so a lighter version was downloaded. However, the full data Excel could not be downloaded automatically.",
						});
					}
				} else {
					Swal.fire({
						icon: "success",
						title: "Downloaded!",
						text: "Invoice PDF downloaded successfully.",
						timer: 1500,
						showConfirmButton: false,
					});
				}

				return;
			}

			console.info("[billing-pdf] S3 cache miss, falling back to backend PDF generation", {
				billingId: id,
				mode: signedRes.data?.mode,
			});

			const res = await api.get(`/billing/${id}/pdf`, {
				responseType: "blob",
				timeout: 900000,
			});

			// Validate: check if backend actually returned a PDF
			const contentType = res.headers["content-type"] || "";
			if (!contentType.includes("application/pdf")) {
				const errorText = await res.data.text();
				Swal.fire({
					icon: "error",
					title: "PDF Generation Failed",
					text: errorText
						? `Server error: ${errorText.slice(0, 300)}`
						: "The server could not generate the PDF. Please try again or contact support.",
				});
				return;
			}

			const url = window.URL.createObjectURL(
				new Blob([res.data], { type: "application/pdf" }),
			);
			const link = document.createElement("a");
			link.href = url;
			link.download = `INVOICE-${id}.pdf`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.setTimeout(() => window.URL.revokeObjectURL(url), 3000);

			// Check for Lite Mode header
			if (res.headers["x-billing-pdf-mode"] === "download-lite") {
				try {
					const excelRes = await api.get(`/billing/${id}/excel`, {
						responseType: "blob",
						timeout: 900000,
					});

					const excelUrl = window.URL.createObjectURL(
						new Blob([excelRes.data], {
							type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
						}),
					);
					const excelLink = document.createElement("a");
					excelLink.href = excelUrl;
					excelLink.download = `INVOICE-${id}-FULL-DATA.xlsx`;
					document.body.appendChild(excelLink);
					excelLink.click();
					document.body.removeChild(excelLink);
					window.setTimeout(() => window.URL.revokeObjectURL(excelUrl), 3000);

					Swal.fire({
						icon: "info",
						title: "Large Invoice Exported",
						text: "A lighter PDF was downloaded because the document is very large. The full billing data has also been downloaded in Excel format.",
					});
				} catch (excelError) {
					console.error("Full data Excel download error:", excelError);
					Swal.fire({
						icon: "warning",
						title: "PDF Downloaded",
						text: "The invoice PDF was very large, so a lighter version was downloaded. However, the full data Excel could not be downloaded automatically.",
					});
				}
			} else {
				Swal.fire({
					icon: "success",
					title: "Downloaded!",
					text: "Invoice PDF downloaded successfully.",
					timer: 1500,
					showConfirmButton: false,
				});
			}
		} catch (err) {
			console.error("Invoice PDF download error:", err);

			Swal.fire({
				icon: "error",
				title: "Download Failed",
				text: "Failed to download Invoice PDF.",
			});
		} finally {
			setDownloadingPdf(false);
		}
	};

	const openPaymentModal = async (bill) => {
		setPaymentBilling(bill);
		setPaymentAmount(
			bill.paidAmount !== undefined && bill.paidAmount !== null
				? String(bill.paidAmount)
				: ""
		);
		setAdvisedInvoiceFile(null);
		setAdvanceSummary({
			totalAdvance: 0,
			advanceDescription: "",
			alreadyDeducted: 0,
			availableAdvance: 0,
		});

		const initDeductions = (dedList) => {
			if (Array.isArray(dedList) && dedList.length > 0) {
				return dedList.map((d) => ({
					deductionType: d.deductionType?._id || d.deductionType || "",
					deductionTypeName: d.deductionTypeName || "",
					amount:
						d.amount !== undefined && d.amount !== null
							? String(d.amount)
							: "",
					remarks: d.remarks || "",
				}));
			}
			return [{ deductionType: "", deductionTypeName: "", amount: "", remarks: "" }];
		};

		setDeductions(initDeductions(bill.deductions));
		setShowPaymentModal(true);

		try {
			const res = await api.get(`/billing/${bill._id}`);
			if (res.data?.data) {
				const freshBill = res.data.data;
				setPaymentBilling(freshBill);
				if (freshBill.paidAmount !== undefined && freshBill.paidAmount !== null) {
					setPaymentAmount(String(freshBill.paidAmount));
				}
				if (Array.isArray(freshBill.deductions) && freshBill.deductions.length > 0) {
					setDeductions(initDeductions(freshBill.deductions));
				}
				if (res.data.advanceSummary) {
					setAdvanceSummary(res.data.advanceSummary);
				} else if (freshBill.workOrder?._id) {
					const advRes = await api.get(
						`/billing/advance-summary/${freshBill.workOrder._id}?excludeBillingId=${freshBill._id}`
					);
					if (advRes.data?.data) {
						setAdvanceSummary(advRes.data.data);
					}
				}
			}
		} catch (e) {
			const woId = bill.workOrder?._id || bill.workOrder;
			if (woId) {
				try {
					const advRes = await api.get(
						`/billing/advance-summary/${woId}?excludeBillingId=${bill._id}`
					);
					if (advRes.data?.data) {
						setAdvanceSummary(advRes.data.data);
					}
				} catch (err) {
					// ignore
				}
			}
		}
	};

	const handleApplyAdvanceDeduction = (availableAmt, currentNet = null) => {
		const recAmt = Number(availableAmt) || 0;
		if (recAmt <= 0) return;

		// Default to applying available advance (or capped at invoice net if provided)
		const amtToApply = currentNet !== null ? Math.min(recAmt, Math.max(0, currentNet)) : recAmt;

		const advType = deductionTypes.find((t) =>
			(t.name || "").toLowerCase().includes("advance")
		);

		setDeductions((prev) => {
			const existingIdx = prev.findIndex((d) => {
				const name = (d.deductionTypeName || "").toLowerCase();
				const rem = (d.remarks || "").toLowerCase();
				return name.includes("advance") || rem.includes("advance");
			});

			if (existingIdx >= 0) {
				const updated = [...prev];
				updated[existingIdx] = {
					...updated[existingIdx],
					deductionType: updated[existingIdx].deductionType || advType?._id || "",
					deductionTypeName: updated[existingIdx].deductionTypeName || "Advance Recovery",
					amount: String(amtToApply),
					remarks: updated[existingIdx].remarks || "Advance Recovery against Work Order",
				};
				return updated;
			}

			if (
				prev.length === 1 &&
				!prev[0].deductionType &&
				!prev[0].amount &&
				!prev[0].deductionTypeName
			) {
				return [
					{
						deductionType: advType?._id || "",
						deductionTypeName: "Advance Recovery",
						amount: String(amtToApply),
						remarks: "Advance Recovery against Work Order",
					},
				];
			}

			return [
				...prev,
				{
					deductionType: advType?._id || "",
					deductionTypeName: "Advance Recovery",
					amount: String(amtToApply),
					remarks: "Advance Recovery against Work Order",
				},
			];
		});
	};

	const handleAddDeductionRow = () => {
		setDeductions((prev) => [
			...prev,
			{ deductionType: "", deductionTypeName: "", amount: "", remarks: "" },
		]);
	};

	const handleRemoveDeductionRow = (index) => {
		setDeductions((prev) => {
			const updated = prev.filter((_, idx) => idx !== index);
			return updated.length > 0
				? updated
				: [{ deductionType: "", deductionTypeName: "", amount: "", remarks: "" }];
		});
	};

	const handleDeductionChange = (index, field, value) => {
		setDeductions((prev) => {
			const updated = [...prev];
			if (field === "deductionType") {
				if (value === "ADVANCE_RECOVERY") {
					const advType = deductionTypes.find((t) =>
						(t.name || "").toLowerCase().includes("advance")
					);
					updated[index] = {
						...updated[index],
						deductionType: advType?._id || "",
						deductionTypeName: "Advance Recovery",
						remarks: updated[index].remarks || "Advance Recovery against Work Order",
					};
				} else {
					const selectedType = deductionTypes.find((t) => t._id === value);
					updated[index] = {
						...updated[index],
						deductionType: value,
						deductionTypeName: selectedType ? selectedType.name : "",
					};
				}
			} else {
				updated[index] = {
					...updated[index],
					[field]: value,
				};
			}
			return updated;
		});
	};

	const handleUpdatePayment = async () => {
		if (!paymentBilling) return;

		try {
			setPaymentSubmitting(true);
			const receivedAmount = Number(paymentAmount || 0);

			const validDeductions = deductions
				.filter(
					(d) =>
						(d.deductionType || d.deductionTypeName) &&
						Number(d.amount) > 0
				)
				.map((d) => ({
					deductionType: d.deductionType || undefined,
					deductionTypeName: d.deductionTypeName || "",
					amount: Number(d.amount) || 0,
					remarks: d.remarks || "",
				}));

			const currentAdvanceDeductionTotal = validDeductions
				.filter(
					(d) =>
						(d.deductionTypeName || "").toLowerCase().includes("advance") ||
						(d.remarks || "").toLowerCase().includes("advance")
				)
				.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

			if (
				advanceSummary?.totalAdvance > 0 &&
				currentAdvanceDeductionTotal > advanceSummary.availableAdvance
			) {
				const confirmRes = await Swal.fire({
					title: "Advance Over-Deduction Warning",
					text: `This bill's advance deduction (₹${currentAdvanceDeductionTotal.toLocaleString()}) exceeds the available advance balance (₹${advanceSummary.availableAdvance.toLocaleString()}). Do you want to proceed?`,
					icon: "warning",
					showCancelButton: true,
					confirmButtonText: "Yes, save anyway",
					cancelButtonText: "No, let me change",
				});
				if (!confirmRes.isConfirmed) {
					setPaymentSubmitting(false);
					return;
				}
			}

			const formData = new FormData();
			formData.append("receivedAmount", receivedAmount);
			formData.append("deductions", JSON.stringify(validDeductions));
			if (advisedInvoiceFile) {
				formData.append("advisedInvoice", advisedInvoiceFile);
			}

			await api.patch(
				`/billing/${paymentBilling._id}/payment`,
				formData,
				{
					headers: { "Content-Type": "multipart/form-data" },
				}
			);

			setShowPaymentModal(false);
			setPaymentBilling(null);
			setPaymentAmount("");
			setAdvisedInvoiceFile(null);
			setDeductions([]);
			setAdvanceSummary({
				totalAdvance: 0,
				advanceDescription: "",
				alreadyDeducted: 0,
				availableAdvance: 0,
			});
			await fetchBillings();

			Swal.fire({
				icon: "success",
				title: "Invoice Advice Updated",
				text: "Billing invoice advice and payment details were updated successfully.",
				timer: 1600,
				showConfirmButton: false,
			});
		} catch (error) {
			Swal.fire(
				"Error",
				error.response?.data?.message || "Failed to update payment advice",
				"error",
			);
		} finally {
			setPaymentSubmitting(false);
		}
	};

	// ---------------------------
	// Delete Billing
	// ---------------------------
	const handleDelete = async (id) => {
		const result = await Swal.fire({
			title: "Delete Billing?",
			text: "This action cannot be undone!",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Yes, delete it!",
			cancelButtonText: "Cancel",
			confirmButtonColor: "#d33",
			cancelButtonColor: "#3085d6",
		});

		if (!result.isConfirmed) return;

		try {
			const response = await api.delete(`/billing/${id}`);
			if (response.data.success) {
				Swal.fire(
					"Deleted!",
					"Billing record deleted successfully.",
					"success",
				);
				fetchBillings();
			}
		} catch (error) {
			Swal.fire(
				"Error",
				error.response?.data?.message || "Failed to delete billing",
				"error",
			);
		}
	};

	// ---------------------------
	// View Billing in Modal
	// ---------------------------
	const handleView = async (id) => {
		try {
			const response = await api.get(`/billing/${id}`);
			if (response.data.success) {
				setSelectedBilling(response.data.data);
				setShowModal(true);
			}
		} catch (error) {
			Swal.fire("Error", "Failed to load billing details", "error");
		}
	};

	// ================= APPROVE =================
	const handleApprove = async (id) => {
		const confirm = await Swal.fire({
			title: "Approve Billing?",
			text: "Signature is required for approval.",
			icon: "info",
			showCancelButton: true,
			confirmButtonText: "Provide Signature",
			cancelButtonText: "Cancel",
		});

		if (!confirm.isConfirmed) return;

		setApproveId(id);
		setShowSignModal(true);
	};

	const handleSaveSignature = async (file) => {
		try {
			setLoading(true);

			const formData = new FormData();
			formData.append("clientSignature", file);

			const res = await api.patch(`/billing/${approveId}/approve`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			if (res.data.success) {
				Swal.fire("Approved!", "Billing approved successfully", "success");
				setShowSignModal(false);
				setApproveId(null);
				fetchBillings();
			}
		} catch (err) {
			Swal.fire(
				"Error",
				err.response?.data?.message || "Failed to approve billing",
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	// ================= REJECT =================
	const handleReject = async (id) => {
		const { value: remarks } = await Swal.fire({
			title: "Reject Billing?",
			input: "textarea",
			inputLabel: "Rejection Reason",
			inputPlaceholder: "Enter reason for rejection...",
			inputAttributes: {
				"aria-label": "Rejection reason",
			},
			showCancelButton: true,
			confirmButtonText: "Reject",
			cancelButtonText: "Cancel",
		});

		if (!remarks) return;

		try {
			const res = await api.patch(`/billing/${id}/reject`, {
				remarks,
			});

			if (res.data.success) {
				Swal.fire("Rejected!", "Billing rejected successfully", "success");
				fetchBillings();
			}
		} catch {
			Swal.fire("Error", "Failed to reject billing", "error");
		}
	};

	const handleOpenEmail = (bill) => {
		const wo = typeof bill.workOrder === "object" ? bill.workOrder : workOrderMap[bill.workOrder];
		setEmailInitialData({
			to: wo?.client?.email || "",
			clientId: wo?.client?._id || wo?.client || "",
			subject: `Invoice for Work Order: ${wo?.woNo || wo?.woNumber || ""}`,
			placeholders: {
				clientName: wo?.client?.name || "",
				projectName: wo?.project?.projectName || "",
				number: wo?.woNo || wo?.woNumber || "",
				type: "Billing",
				invoiceNumber: bill.invoiceNumber || "",
				amount: bill.totalAmount || "",
				dueDate: bill.dueDate ? new Date(bill.dueDate).toISOString().split("T")[0] : "",
			}
		});
		setShowEmailModal(true);
	};

	// ---------------------------
	// Helpers
	// ---------------------------
	const formatDate = (dateString) => {
		if (!dateString) return "—";
		const date = new Date(dateString);
		return date.toLocaleDateString("en-IN", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const formatCurrency = (amount) =>
		`₹ ${parseFloat(amount || 0).toLocaleString("en-IN", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`;

	const getPendingAmount = (bill) =>
		Math.max(
			Number(bill.totalAmount || 0) -
			(Number(bill.paidAmount || 0) + Number(bill.totalDeductionAmount || 0)),
			0
		);

	const getStatusBadge = (status) => {
		switch (status) {
			case "Draft":
				return "secondary";
			case "Submitted":
				return "warning";
			case "Approved":
				return "info";
			case "Rejected":
				return "danger";
			case "Paid":
				return "success";
			case "Partially Paid":
				return "primary";
			default:
				return "secondary";
		}
	};

	const svgMenu = (
		<svg width="20px" height="20px" viewBox="0 0 24 24">
			<circle fill="#000000" cx="5" cy="12" r="2"></circle>
			<circle fill="#000000" cx="12" cy="12" r="2"></circle>
			<circle fill="#000000" cx="19" cy="12" r="2"></circle>
		</svg>
	);

	return (
		<Fragment>
			<PageTitle activeMenu="Billing" motherMenu="Billing Management" />

			{error && (
				<Alert variant="danger" dismissible onClose={() => setError("")}>
					<i className="fas fa-exclamation-triangle me-2"></i>
					{error}
				</Alert>
			)}
			{/* ================= FILTER BAR ================= */}
			<Row className="mb-3">
				<Col lg={12}>
					<Card>
						<Card.Body>
							<Row className="g-3">
								{/* SEARCH */}
								<Col md={3}>
									<label className="form-label">Search Invoice</label>
									<input
										type="text"
										className="form-control form-control-sm"
										placeholder="Invoice number..."
										value={filters.search}
										onChange={(e) =>
											setFilters((p) => ({
												...p,
												search: e.target.value,
												page: 1,
											}))
										}
									/>
								</Col>

								{/* PROJECT */}
								<Col md={3}>
									<label className="form-label">Project</label>
									<select
										className="form-select form-select-sm"
										value={filters.project}
										onChange={(e) =>
											setFilters((p) => ({
												...p,
												project: e.target.value,
												workOrder: "",
												page: 1,
											}))
										}
									>
										<option value="">All Projects</option>
										{projects.map((p) => (
											<option key={p._id} value={p._id}>
												{p.projectName || p.name || p.projectCode || p._id}
											</option>
										))}
									</select>
								</Col>

								{/* WORK ORDER */}
								<Col md={3}>
									<label className="form-label">Work Order</label>
									<select
										className="form-select form-select-sm"
										value={filters.workOrder}
										onChange={(e) =>
											setFilters((p) => ({
												...p,
												workOrder: e.target.value,
												page: 1,
											}))
										}
									>
										<option value="">All Work Orders</option>
										{availableWorkOrders.map((wo) => (
											<option key={wo._id} value={wo._id}>
												{wo.woNo || wo.woNumber}{wo.label ? ` – ${wo.label}` : ""}
											</option>
										))}
									</select>
								</Col>

								{/* STATUS */}
								<Col md={2}>
									<label className="form-label">Status</label>
									<select
										className="form-select form-select-sm"
										value={filters.status}
										onChange={(e) =>
											setFilters((p) => ({
												...p,
												status: e.target.value,
												page: 1,
											}))
										}
									>
										<option value="">All</option>
										<option value="Draft">Draft</option>
										<option value="Submitted">Submitted</option>
										<option value="Approved">Approved</option>
										<option value="Partially Paid">Partially Paid</option>
										<option value="Paid">Paid</option>
										<option value="Rejected">Rejected</option>
									</select>
								</Col>

								{/* FROM DATE */}
								<Col md={2}>
									<label className="form-label">From Date</label>
									<input
										type="date"
										className="form-control form-control-sm"
										value={filters.fromDate}
										onChange={(e) =>
											setFilters((p) => ({
												...p,
												fromDate: e.target.value,
												page: 1,
											}))
										}
									/>
								</Col>

								{/* TO DATE */}
								<Col md={2}>
									<label className="form-label">To Date</label>
									<input
										type="date"
										className="form-control form-control-sm"
										value={filters.toDate}
										onChange={(e) =>
											setFilters((p) => ({
												...p,
												toDate: e.target.value,
												page: 1,
											}))
										}
									/>
								</Col>

								{/* PER PAGE */}
								<Col md={2}>
									<label className="form-label">Per Page</label>
									<select
										className="form-select form-select-sm"
										value={filters.limit}
										onChange={(e) =>
											setFilters((p) => ({
												...p,
												limit: Number(e.target.value),
												page: 1,
											}))
										}
									>
										<option value={10}>10</option>
										<option value={25}>25</option>
										<option value={50}>50</option>
									</select>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			{/* List Table */}
			<Row>
				<Col lg={12}>
					<Card>
						<Card.Header className="d-flex justify-content-between align-items-center">
							<Card.Title>Billing / Invoice List</Card.Title>
							{canAddBilling && (
								<Link
									to={
										workOrderFromUrl || projectFromUrl
											? `/billings/add?${projectFromUrl ? `project=${projectFromUrl}&` : ""}${workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : ""}`
											: "/billings/add"
									}
									className="btn btn-primary"
								>
									<i className="fas fa-plus me-2"></i>Add Billing
								</Link>
							)}
						</Card.Header>

						<Card.Body>
							{/* Loading */}
							{loading ? (
								<div className="text-center py-5">
									<div
										className="spinner-border text-primary"
										role="status"
									></div>
									<p className="mt-3">Loading billings...</p>
								</div>
							) : billings.length === 0 ? (
								<div className="text-center py-5">
									<i className="fas fa-file-invoice fa-3x text-muted mb-3"></i>
									<p className="text-muted">No billings found.</p>
									{canAddBilling && (
										<Link
											to={
												workOrderFromUrl
													? `/billings/add?${projectFromUrl ? `project=${projectFromUrl}&` : ""}workOrder=${workOrderFromUrl}`
													: "/billings/add"
											}
											className="btn btn-primary"
										>
											<i className="fas fa-plus me-2"></i>Add Billing
										</Link>
									)}
								</div>
							) : (
								<div className="table-responsive">
									<Table hover className="align-middle text-nowrap">
										<thead className="table-light">
											<tr className="text-center">
												<th style={{ width: "50px" }}>#</th>
												<th>Invoice No</th>
												<th style={{ minWidth: "160px" }}>Work Order</th>
												<th style={{ width: "140px" }}>Stage</th>
												<th style={{ width: "160px" }}>Total</th>
												<th style={{ width: "160px" }}>Paid</th>
												<th style={{ width: "160px" }}>Pending</th>
												<th style={{ width: "130px" }}>Status</th>
												<th style={{ width: "130px" }}>Date</th>
												<th className="text-center">Verified Copy</th>
												<th style={{ width: "90px" }}>Actions</th>
											</tr>
										</thead>

										<tbody>
											{billings.map((bill, i) => (
												<tr key={bill._id}>
													<td>{(filters.page - 1) * filters.limit + (i + 1)}</td>
													<td>
														<div className="d-flex align-items-center justify-content-center gap-1">
															<strong className="text-primary">
																{bill.invoiceNumber}
															</strong>
															{bill.advisedInvoice?.fileUrl && (
																<a
																	href={getFileUrl(bill.advisedInvoice.fileUrl)}
																	target="_blank"
																	rel="noopener noreferrer"
																	className="badge bg-info-subtle text-info border border-info ms-1"
																	style={{ fontSize: "10px", textDecoration: "none" }}
																	title={`Advised Invoice: ${bill.advisedInvoice.fileName || "View Document"}`}
																>
																	<i className="fas fa-file-invoice me-1"></i>Advice
																</a>
															)}
														</div>
													</td>
													<td>
														{bill.workOrder &&
															typeof bill.workOrder === "object"
															? bill.workOrder.woNo ||
															bill.workOrder.woNumber ||
															"—"
															: workOrderMap[bill.workOrder]?.woNo ||
															workOrderMap[bill.workOrder]?.woNumber ||
															"—"}
													</td>

													<td>
														{bill.stages && bill.stages.length > 0 ? (
															<Dropdown>
																<Dropdown.Toggle size="sm" className="border">
																	{bill.stages.length} Stage
																	{bill.stages.length > 1 ? "s" : ""}
																</Dropdown.Toggle>

																<Dropdown.Menu as={PortalMenu}>
																	{bill.stages.map((stage, index) => (
																		<Dropdown.Item key={index} disabled>
																			{stage}
																		</Dropdown.Item>
																	))}
																</Dropdown.Menu>
															</Dropdown>
														) : (
															<Badge bg="secondary">—</Badge>
														)}
													</td>

													<td>{formatCurrency(bill.totalAmount)}</td>
													<td>
														<div>{formatCurrency(bill.paidAmount)}</div>
														{Number(bill.totalDeductionAmount) > 0 && (
															<div
																className="badge bg-light text-danger border border-danger mt-1 d-inline-flex align-items-center"
																style={{ fontSize: "11px", fontWeight: "600" }}
																title={`Deductions: ${bill.deductions && bill.deductions.length > 0
																	? bill.deductions
																		.map(
																			(d) =>
																				`${d.deductionTypeName || "Deduction"}: ${formatCurrency(d.amount)}`
																		)
																		.join(", ")
																	: formatCurrency(bill.totalDeductionAmount)
																	}`}
															>
																<i className="fas fa-minus-circle me-1"></i>
																Ded: {formatCurrency(bill.totalDeductionAmount)}
															</div>
														)}
													</td>
													<td>{formatCurrency(getPendingAmount(bill))}</td>
													<td>
														<Badge bg={getStatusBadge(bill.status)}>
															{bill.status}
														</Badge>
													</td>
													<td>{formatDate(bill.createdAt)}</td>
													<td className="text-center">
														<VerifiedCopyActions
															id={bill._id}
															type="billing"
															hasVerifiedCopy={
																!!(bill.verifiedCopy?.filePath || bill.verifiedCopy?.fileUrl)
															}
															verifiedCopy={bill.verifiedCopy}
															onUploadSuccess={fetchBillings}
															canUpload={canManageBillings}
														/>
													</td>
													<td>
														<Dropdown drop="start i-false">
															<Dropdown.Toggle
																as="div"
																className="btn-link i-false"
																style={{ cursor: "pointer" }}
															>
																{svgMenu}
															</Dropdown.Toggle>
															<Dropdown.Menu align="end" as={PortalMenu}>
																<PermissionWrapper user={user} required={["read:billings"]}>
																	<Dropdown.Item
																		onClick={() => handleView(bill._id)}
																		title="View full billing details"
																	>
																		<i className="fas fa-eye me-2 text-primary"></i>View
																		Details
																	</Dropdown.Item>
																</PermissionWrapper>

																{canAddBilling && (
																	<Dropdown.Item
																		as={Link}
																		to={
																			`/billings/edit/${bill._id}?${projectFromUrl ? `project=${projectFromUrl}&` : (bill.project?._id || bill.project ? `project=${bill.project?._id || bill.project}&` : "")}${workOrderFromUrl ? `workOrder=${workOrderFromUrl}` : (bill.workOrder?._id || bill.workOrder ? `workOrder=${bill.workOrder?._id || bill.workOrder}` : "")}`
																		}
																		title="Edit billing details"
																	>
																		<i className="fas fa-edit me-2 text-warning"></i>Edit
																	</Dropdown.Item>
																)}

																<PermissionWrapper user={user} required={["edit:billings"]}>
																	<Dropdown.Item
																		onClick={() => openPaymentModal(bill)}
																		title="Update payment advice, deductions and payment details"
																	>
																		<i className="fas fa-file-invoice-dollar me-2 text-success"></i>
																		Update Payment Advice
																	</Dropdown.Item>
																</PermissionWrapper>

																{bill.advisedInvoice?.fileUrl && (
																	<Dropdown.Item
																		as="a"
																		href={getFileUrl(bill.advisedInvoice.fileUrl)}
																		target="_blank"
																		rel="noopener noreferrer"
																		title="View or download uploaded advised invoice"
																	>
																		<i className="fas fa-file-download me-2 text-info"></i>
																		View Advised Invoice
																	</Dropdown.Item>
																)}

																<Dropdown.Divider />

																{["Draft", "Submitted", "Pending"].includes(bill.status) && (
																	<>
																		<PermissionWrapper user={user} required={["approve:billings"]}>
																			<Dropdown.Item
																				className="text-success"
																				onClick={() => handleApprove(bill._id)}
																				title="Approve this invoice"
																			>
																				<i className="fas fa-check me-2"></i>
																				Approve
																			</Dropdown.Item>
																		</PermissionWrapper>

																		<PermissionWrapper user={user} required={["reject:billings"]}>
																			<Dropdown.Item
																				className="text-danger"
																				onClick={() => handleReject(bill._id)}
																				title="Reject this invoice"
																			>
																				<i className="fas fa-times me-2"></i>
																				Reject
																			</Dropdown.Item>
																		</PermissionWrapper>

																		<Dropdown.Divider />
																	</>
																)}

																<PermissionWrapper user={user} required={["read:billings"]}>
																	<Dropdown.Item
																		onClick={() => handleDownloadExcel(bill._id)}
																		title="Download Tax Invoice in Excel format"
																	>
																		<i className="fas fa-file-excel me-2 text-success"></i>
																		Download Tax Invoice
																	</Dropdown.Item>
																	<Dropdown.Item
																		onClick={() => handleDownloadPIExcel(bill._id)}
																		title="Download Proforma Invoice in Excel format"
																	>
																		<i className="fas fa-file-excel me-2 text-success"></i>
																		Download Proforma Invoice

																	</Dropdown.Item>

																	<Dropdown.Divider />

																	<Dropdown.Item
																		onClick={() => handlePreviewPDF(bill._id)}
																		disabled={previewingPdf}
																		title="Preview system-generated invoice PDF"
																	>
																		<i className="fas fa-eye me-2 text-info"></i>
																		Preview PDF
																	</Dropdown.Item>

																	<PermissionWrapper user={user} required={["send:email"]}>
																		<Dropdown.Item
																			onClick={() => handleOpenEmail(bill)}
																			title="Send invoice via email"
																		>
																			<i className="fas fa-envelope me-2 text-info"></i>
																			Send Email
																		</Dropdown.Item>
																	</PermissionWrapper>

																	<Dropdown.Item
																		onClick={() => handleDownloadPDF(bill._id)}
																		disabled={downloadingPdf}
																		title="Download system-generated invoice PDF"
																	>
																		<i className="fas fa-file-pdf me-2 text-danger"></i>
																		{downloadingPdf
																			? "Downloading..."
																			: "Download PDF"}
																	</Dropdown.Item>
																</PermissionWrapper>

																<Dropdown.Divider />
																<PermissionWrapper user={user} required={["delete:billings"]}>
																	<Dropdown.Item
																		className="text-danger"
																		onClick={() => handleDelete(bill._id)}
																	>
																		<i className="fas fa-trash me-2"></i>Delete
																	</Dropdown.Item>
																</PermissionWrapper>
															</Dropdown.Menu>
														</Dropdown>
													</td>
												</tr>
											))}
										</tbody>
									</Table>
								</div>
							)}
							{/* Pagination */}
							{!loading && billings.length > 0 && (
								<AppPagination
									currentPage={filters.page}
									totalPages={pagination.totalPages}
									totalCount={pagination.totalCount}
									itemsLength={billings.length}
									limit={filters.limit}
									onPageChange={handlePageChange}
								/>
							)}
						</Card.Body>
					</Card>
				</Col>
			</Row>

			{/* Billing Details Modal */}
			<Modal
				show={showModal}
				onHide={() => setShowModal(false)}
				centered
				size="lg"
			>
				<Modal.Header closeButton>
					<Modal.Title>Billing Details</Modal.Title>
				</Modal.Header>

				<Modal.Body>
					{selectedBilling && (
						<>
							<Row>
								<Col md={6}>
									<p>
										<strong>Invoice Number:</strong>{" "}
										{selectedBilling.invoiceNumber}
									</p>
									<p>
										<strong>Work Order:</strong>{" "}
										{selectedBilling.workOrder?.woNo || "—"}
									</p>
									<p>
										<strong>Stages:</strong>{" "}
										{selectedBilling.stages?.length
											? selectedBilling.stages.join(", ")
											: "—"}
									</p>

									{/* Linked Report */}
									{selectedBilling.linkedReport?.reportType && (
										<p>
											<strong>Linked Report:</strong>{" "}
											{selectedBilling.linkedReport.reportType === "MEASUREMENT_SHEET" ? "JMR" : selectedBilling.linkedReport.reportType} —{" "}
											{selectedBilling.linkedReport?.reportNumber ||
												selectedBilling.linkedReport?.reportId ||
												"—"}
										</p>
									)}
								</Col>

								<Col md={6}>
									<p>
										<strong>Status:</strong> {selectedBilling.status}
									</p>
									<p>
										<strong>Total Amount:</strong>{" "}
										{formatCurrency(selectedBilling.totalAmount)}
									</p>
									<p>
										<strong>Paid Amount:</strong>{" "}
										{formatCurrency(selectedBilling.paidAmount)}
									</p>
									<p>
										<strong>Pending Amount:</strong>{" "}
										{formatCurrency(getPendingAmount(selectedBilling))}
									</p>
									<p>
										<strong>Date:</strong>{" "}
										{formatDate(selectedBilling.createdAt)}
									</p>
								</Col>
							</Row>

							{/* Remarks */}
							{selectedBilling.remarks && (
								<>
									<hr />
									<p>
										<strong>Remarks:</strong>
									</p>
									<p className="text-muted">{selectedBilling.remarks}</p>
								</>
							)}

							{/* Line Items */}
							{selectedBilling.lineItems?.length > 0 && (
								<>
									<hr />
									<h5>Line Items</h5>
									<Table bordered hover size="sm">
										<thead>
											<tr>
												<th>#</th>
												<th>Description</th>
												<th>Qty</th>
												<th>Unit</th>
												<th>Rate</th>
												<th>Amount</th>
											</tr>
										</thead>
										<tbody>
											{selectedBilling.lineItems.map((item, i) => (
												<tr key={i}>
													<td>{i + 1}</td>
													<td>{item.description}</td>
													<td>{item.quantity}</td>
													<td>{item.unit}</td>
													<td>{formatCurrency(item.rate)}</td>
													<td>{formatCurrency(item.amount)}</td>
												</tr>
											))}
										</tbody>
									</Table>
								</>
							)}

							{/* Advised Invoice & Deductions in View Details */}
							{(selectedBilling.totalDeductionAmount > 0 ||
								(selectedBilling.deductions && selectedBilling.deductions.length > 0) ||
								selectedBilling.advisedInvoice?.fileUrl) && (
									<>
										<hr />
										<h5>Invoice Advice & Deductions</h5>
										{selectedBilling.advisedInvoice?.fileUrl && (
											<p className="mb-3">
												<strong>Advised Invoice:</strong>{" "}
												<a
													href={getFileUrl(selectedBilling.advisedInvoice.fileUrl)}
													target="_blank"
													rel="noopener noreferrer"
													className="btn btn-outline-primary btn-sm ms-2"
												>
													<i className="fas fa-file-download me-1"></i>
													{selectedBilling.advisedInvoice.fileName || "View Advised Invoice"}
												</a>
											</p>
										)}

										{selectedBilling.deductions && selectedBilling.deductions.length > 0 && (
											<div className="table-responsive">
												<Table bordered hover size="sm">
													<thead className="table-light">
														<tr>
															<th>#</th>
															<th>Deduction Type</th>
															<th>Amount</th>
															<th>Remarks</th>
														</tr>
													</thead>
													<tbody>
														{selectedBilling.deductions.map((d, idx) => (
															<tr key={idx}>
																<td>{idx + 1}</td>
																<td>
																	{d.deductionTypeName || d.deductionType?.name || "—"}
																</td>
																<td className="text-danger fw-bold">
																	{formatCurrency(d.amount)}
																</td>
																<td>{d.remarks || "—"}</td>
															</tr>
														))}
														<tr className="table-secondary fw-bold">
															<td colSpan={2} className="text-end">
																Total Deductions:
															</td>
															<td className="text-danger">
																{formatCurrency(selectedBilling.totalDeductionAmount)}
															</td>
															<td></td>
														</tr>
													</tbody>
												</Table>
											</div>
										)}
									</>
								)}
						</>
					)}
				</Modal.Body>

				<Modal.Footer>
					<Button variant="secondary" onClick={() => setShowModal(false)}>
						Close
					</Button>
				</Modal.Footer>
			</Modal>

			{/* ================= UPDATE PAYMENT ADVICE MODAL ================= */}
			<Modal
				show={showPaymentModal}
				onHide={() => setShowPaymentModal(false)}
				size="lg"
				centered
				scrollable
			>
				<Modal.Header closeButton className="bg-light py-3 px-4">
					<Modal.Title className="h5 mb-0">
						<i className="fas fa-file-invoice-dollar me-2 text-primary"></i>
						Update Payment Advice
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="p-3 p-md-4">
					{paymentBilling && (() => {
						const currentDeductionsTotal = deductions.reduce(
							(sum, row) => sum + (Number(row.amount) || 0),
							0
						);
						const currentReceived = Number(paymentAmount) || 0;
						const currentSettled = currentReceived + currentDeductionsTotal;
						const currentNetPayable = Math.max(
							0,
							(paymentBilling.totalAmount || 0) - currentDeductionsTotal
						);
						const currentBalance = Math.max(
							0,
							(paymentBilling.totalAmount || 0) - currentSettled
						);

						const currentAdvanceDeductionsTotal = deductions
							.filter((d) => {
								const name = (d.deductionTypeName || "").toLowerCase();
								const rem = (d.remarks || "").toLowerCase();
								return name.includes("advance") || rem.includes("advance");
							})
							.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

						const totalWOAdvance = Number(
							advanceSummary?.totalAdvance || paymentBilling.workOrder?.advanceAmount || 0
						);
						const otherBillsRecovered = Number(advanceSummary?.alreadyDeducted || 0);
						const availableAdvanceToDeduct = Math.max(0, totalWOAdvance - otherBillsRecovered);
						const remainingWOAdvanceAfterThisBill = Math.max(
							0,
							availableAdvanceToDeduct - currentAdvanceDeductionsTotal
						);
						const isAdvanceOverLimit = currentAdvanceDeductionsTotal > availableAdvanceToDeduct;

						let calculatedStatus = "Draft";
						if (currentReceived === 0 && currentDeductionsTotal === 0) {
							calculatedStatus = paymentBilling.status || "Approved";
						} else if (
							currentSettled >= (paymentBilling.totalAmount || 0) ||
							currentReceived >= (paymentBilling.totalAmount || 0)
						) {
							calculatedStatus = "Paid";
						} else {
							calculatedStatus = "Partially Paid";
						}

						return (
							<>
								{/* Top Invoice Overview */}
								<div className="bg-light rounded p-3 mb-3 border">
									<Row className="g-2 text-center text-sm-start">
										<Col xs={6} sm={3}>
											<div className="text-muted small">Invoice No</div>
											<strong className="text-primary fs-14">{paymentBilling.invoiceNumber}</strong>
										</Col>
										<Col xs={6} sm={3}>
											<div className="text-muted small">Invoice Total</div>
											<strong className="fs-14">{formatCurrency(paymentBilling.totalAmount)}</strong>
										</Col>
										<Col xs={6} sm={3}>
											<div className="text-muted small">Current Paid</div>
											<strong className="fs-14 text-success">{formatCurrency(paymentBilling.paidAmount)}</strong>
										</Col>
										<Col xs={6} sm={3}>
											<div className="text-muted small">Current Status</div>
											<Badge bg={getStatusBadge(paymentBilling.status)}>
												{paymentBilling.status}
											</Badge>
										</Col>
									</Row>
								</div>

								{/* Work Order Advance Status Banner (if WO has advance configured) */}
								{totalWOAdvance > 0 && (
									<Card
										className="border mb-3 shadow-sm"
										style={{
											background: "linear-gradient(135deg, #fffcf0 0%, #fff9e6 100%)",
											borderColor: "#ffe082",
										}}
									>
										<Card.Body className="p-3">
											<div className="d-flex flex-wrap justify-content-between align-items-center mb-2 pb-2 border-bottom border-warning-subtle">
												<div className="d-flex align-items-center">
													<span
														className="rounded-circle bg-warning text-dark d-inline-flex align-items-center justify-content-center me-2"
														style={{ width: "28px", height: "28px" }}
													>
														<i className="fas fa-hand-holding-usd fs-13"></i>
													</span>
													<div>
														<h6 className="mb-0 fw-bold text-dark fs-14">
															Work Order Advance Status
														</h6>
														{advanceSummary.advanceDescription && (
															<small className="text-muted">
																{advanceSummary.advanceDescription}
															</small>
														)}
													</div>
												</div>
												{availableAdvanceToDeduct > 0 && (
													<Button
														variant="warning"
														size="sm"
														className="fw-bold text-dark shadow-sm px-3 py-1 mt-1 mt-sm-0"
														onClick={() =>
															handleApplyAdvanceDeduction(
																availableAdvanceToDeduct,
																currentNetPayable
															)
														}
														title="Apply remaining available advance recovery into deductions"
													>
														<i className="fas fa-plus-circle me-1"></i>
														+ Apply Advance Recovery
													</Button>
												)}
											</div>

											<Row className="g-2 text-center text-sm-start">
												<Col xs={6} sm={3}>
													<div className="text-muted small">Total WO Advance</div>
													<strong className="fs-14 text-dark">
														{formatCurrency(totalWOAdvance)}
													</strong>
												</Col>
												<Col xs={6} sm={3}>
													<div className="text-muted small">Deducted (Other Bills)</div>
													<strong className="fs-14 text-secondary">
														{formatCurrency(otherBillsRecovered)}
													</strong>
												</Col>
												<Col xs={6} sm={3}>
													<div className="text-muted small">Available for Recovery</div>
													<strong className="fs-14 text-primary">
														{formatCurrency(availableAdvanceToDeduct)}
													</strong>
												</Col>
												<Col xs={6} sm={3}>
													<div className="text-muted small">Balance Advance Left</div>
													<strong
														className={`fs-14 ${remainingWOAdvanceAfterThisBill > 0
															? "text-success"
															: "text-muted"
															}`}
													>
														{formatCurrency(remainingWOAdvanceAfterThisBill)}
													</strong>
												</Col>
											</Row>

											{isAdvanceOverLimit && (
												<Alert variant="danger" className="py-1 px-2 mb-0 mt-2 fs-12 fw-semibold">
													<i className="fas fa-exclamation-triangle me-1"></i>
													Warning: This bill's advance deduction ({formatCurrency(currentAdvanceDeductionsTotal)}) exceeds available advance ({formatCurrency(availableAdvanceToDeduct)}).
												</Alert>
											)}
										</Card.Body>
									</Card>
								)}

								{/* Side-by-side: Received Amount & Advised Invoice Document */}
								<Row className="g-3 mb-3">
									<Col md={6}>
										<Card className="border shadow-none mb-0 h-100">
											<Card.Header className="bg-light py-2 px-3">
												<h6 className="mb-0 fs-14 font-w600">
													<i className="fas fa-wallet me-2 text-success"></i>
													Payment Received / Advised
												</h6>
											</Card.Header>
											<Card.Body className="p-3">
												<Form.Group>
													<Form.Label className="small text-muted mb-1">
														Received Amount (₹)
													</Form.Label>
													<Form.Control
														type="number"
														min="0"
														step="0.01"
														placeholder="Enter received amount"
														value={paymentAmount}
														onChange={(e) => setPaymentAmount(e.target.value)}
													/>
													<Form.Text className="text-muted small">
														Actual payment credited or advised by client.
													</Form.Text>
												</Form.Group>
											</Card.Body>
										</Card>
									</Col>

									<Col md={6}>
										<Card className="border shadow-none mb-0 h-100">
											<Card.Header className="bg-light py-2 px-3 d-flex justify-content-between align-items-center">
												<h6 className="mb-0 fs-14 font-w600">
													<i className="fas fa-file-upload me-2 text-primary"></i>
													Advised Invoice Document
												</h6>
												{paymentBilling.advisedInvoice?.fileUrl && (
													<a
														href={getFileUrl(paymentBilling.advisedInvoice.fileUrl)}
														target="_blank"
														rel="noopener noreferrer"
														className="badge bg-info text-white text-decoration-none"
														title="View uploaded document"
													>
														<i className="fas fa-external-link-alt me-1"></i>View Uploaded
													</a>
												)}
											</Card.Header>
											<Card.Body className="p-3">
												<Form.Group>
													<Form.Label className="small text-muted mb-1">
														Upload Document (PDF / Image)
													</Form.Label>
													<Form.Control
														type="file"
														accept=".pdf,.jpg,.jpeg,.png"
														onChange={(e) =>
															setAdvisedInvoiceFile(e.target.files[0] || null)
														}
													/>
													{advisedInvoiceFile ? (
														<Form.Text className="text-success d-block mt-1">
															<i className="fas fa-check-circle me-1"></i>
															{advisedInvoiceFile.name} ({(advisedInvoiceFile.size / 1024).toFixed(1)} KB)
														</Form.Text>
													) : paymentBilling?.advisedInvoice?.fileUrl ? (
														<Form.Text className="text-primary d-block mt-1">
															<i className="fas fa-paperclip me-1"></i>
															Uploaded:{" "}
															<a
																href={getFileUrl(paymentBilling.advisedInvoice.fileUrl)}
																target="_blank"
																rel="noopener noreferrer"
																className="fw-semibold text-decoration-underline"
															>
																{paymentBilling.advisedInvoice.fileName || "View Document"}
															</a>
														</Form.Text>
													) : null}
												</Form.Group>
											</Card.Body>
										</Card>
									</Col>
								</Row>

								{/* Deductions Breakdown */}
								<Card className="border shadow-none mb-3">
									<Card.Header className="bg-light py-2 px-3 d-flex justify-content-between align-items-center">
										<h6 className="mb-0 fs-14 font-w600">
											<i className="fas fa-minus-circle me-2 text-danger"></i>
											Invoice Deductions
										</h6>
										<Button
											variant="outline-primary"
											size="sm"
											onClick={handleAddDeductionRow}
										>
											<i className="fas fa-plus me-1"></i> Add Deduction
										</Button>
									</Card.Header>
									<Card.Body className="p-3">
										{deductions.map((row, idx) => (
											<Row key={idx} className="g-2 mb-2 align-items-end">
												<Col md={4}>
													{idx === 0 && (
														<Form.Label className="small text-muted mb-1">
															Deduction Type
														</Form.Label>
													)}
													<Form.Select
														value={
															row.deductionType ||
															(row.deductionTypeName === "Advance Recovery"
																? "ADVANCE_RECOVERY"
																: "")
														}
														onChange={(e) =>
															handleDeductionChange(idx, "deductionType", e.target.value)
														}
													>
														<option value="">Select Type...</option>
														<option value="ADVANCE_RECOVERY">
															Advance Recovery (WO Advance)
														</option>
														{deductionTypes
															.filter(
																(t) =>
																	!(t.name || "")
																		.toLowerCase()
																		.includes("advance recovery")
															)
															.map((type) => (
																<option key={type._id} value={type._id}>
																	{type.name}{" "}
																	{type.percentage ? `(${type.percentage}%)` : ""}
																</option>
															))}
														{row.deductionType &&
															row.deductionTypeName &&
															row.deductionTypeName !== "Advance Recovery" &&
															!deductionTypes.some(
																(t) => t._id === row.deductionType
															) && (
																<option value={row.deductionType}>
																	{row.deductionTypeName}
																</option>
															)}
													</Form.Select>
												</Col>
												<Col md={3}>
													{idx === 0 && (
														<Form.Label className="small text-muted mb-1">
															Amount (₹)
														</Form.Label>
													)}
													<Form.Control
														type="number"
														min="0"
														step="0.01"
														placeholder="0.00"
														value={row.amount}
														onChange={(e) =>
															handleDeductionChange(idx, "amount", e.target.value)
														}
													/>
												</Col>
												<Col md={4}>
													{idx === 0 && (
														<Form.Label className="small text-muted mb-1">
															Remarks
														</Form.Label>
													)}
													<Form.Control
														type="text"
														placeholder="e.g. TDS, Retention, Advance Recovery"
														value={row.remarks}
														onChange={(e) =>
															handleDeductionChange(idx, "remarks", e.target.value)
														}
													/>
												</Col>
												<Col md={1}>
													<Button
														variant="outline-danger"
														className="btn-input-height w-100 p-0"
														style={{ minHeight: "3rem", borderRadius: "1rem" }}
														title="Remove row"
														onClick={() => handleRemoveDeductionRow(idx)}
														disabled={
															deductions.length === 1 &&
															!row.deductionType &&
															!row.amount
														}
													>
														<i className="fas fa-trash-alt fs-16"></i>
													</Button>
												</Col>
											</Row>
										))}

										<div className="d-flex justify-content-end align-items-center mt-2 pt-2 border-top">
											<span className="me-2 text-muted fw-bold">Total Deductions:</span>
											<span className="text-danger fw-bold fs-15">
												-{formatCurrency(currentDeductionsTotal)}
											</span>
										</div>
									</Card.Body>
								</Card>

								{/* Settlement Calculation Preview Box */}
								<div className="bg-light rounded p-3 border">
									<Row className="g-3 align-items-center">
										<Col sm={6} md={3}>
											<div className="text-muted small">Invoice Total</div>
											<strong className="fs-14">{formatCurrency(paymentBilling.totalAmount)}</strong>
										</Col>
										<Col sm={6} md={2}>
											<div className="text-muted small">Deductions</div>
											<span className="text-danger fw-bold fs-14">
												-{formatCurrency(currentDeductionsTotal)}
											</span>
										</Col>
										<Col sm={6} md={3}>
											<div className="text-muted small">Net Advised / Payable</div>
											<strong className="text-primary fs-14">{formatCurrency(currentNetPayable)}</strong>
										</Col>
										<Col sm={6} md={2}>
											<div className="text-muted small">Remaining Balance</div>
											<strong className={`fs-14 ${currentBalance > 0 ? "text-danger" : "text-success"}`}>
												{formatCurrency(currentBalance)}
											</strong>
										</Col>
										<Col sm={12} md={2} className="text-md-end">
											<div className="text-muted small mb-1">New Status</div>
											<Badge bg={getStatusBadge(calculatedStatus)} className="fs-12 px-2 py-1">
												{calculatedStatus}
											</Badge>
										</Col>
									</Row>
								</div>
							</>
						);
					})()}
				</Modal.Body>
				<Modal.Footer className="py-3 px-4">
					<Button
						variant="secondary"
						onClick={() => setShowPaymentModal(false)}
						disabled={paymentSubmitting}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={handleUpdatePayment}
						disabled={paymentSubmitting}
					>
						{paymentSubmitting ? (
							<>
								<span
									className="spinner-border spinner-border-sm me-2"
									role="status"
									aria-hidden="true"
								></span>
								Saving...
							</>
						) : (
							"Save Invoice Advice"
						)}
					</Button>
				</Modal.Footer>
			</Modal>

			{/* ================= SIGNATURE MODAL ================= */}
			<SignatureModal
				show={showSignModal}
				onHide={() => {
					setShowSignModal(false);
					setApproveId(null);
				}}
				onSave={handleSaveSignature}
				title="Client Signature for Billing Approval"
			/>

			{/* =========================
 PDF LOADER (Preview or Download)
========================== */}
			{(previewingPdf || downloadingPdf) && (
				<div
					className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
					style={{
						background: "rgba(0,0,0,0.4)",
						zIndex: 9999,
					}}
				>
					<div className="bg-white p-4 rounded shadow text-center">
						<div className="spinner-border text-primary mb-3"></div>
						<h6 className="mb-0">
							{previewingPdf ? "Preparing Invoice PDF Preview…" : "Generating Invoice PDF for Download…"}
						</h6>
						<p className="small text-muted mt-2 mb-0">This may take a few minutes for large documents.</p>
					</div>
				</div>
			)}

			<EmailModal
				show={showEmailModal}
				onHide={() => setShowEmailModal(false)}
				initialData={emailInitialData}
			/>
		</Fragment>
	);
};

export default BillingList;
